import { Link } from 'react-router-dom';
import { t, type DocumentJournalAnswer, type DocumentJournalEntry, type JournalSourceValue } from '@bms2/shared';
import { Money } from '../../components/Money';
import { LoadFailure } from '../../components/composites';
import { DocumentLines, type DocumentLinesDescriptor } from '../records/DocumentViewPage';
import { useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import { MANUAL_JOURNAL_ROUTE, documentJournalPath } from './api';
import styles from './AccountJournalTile.module.css';

/**
 * 🔒 The Account Journal tile (piece 23, decision 1075; T432): what a document
 * posted and where each amount was placed. One heading per `JournalEntry` the
 * document is the source of, with its number and date, then Account, Debit and
 * Credit with 0.00 on the side that does not apply, footed. A reversal stands
 * under the entry it reverses, and each entry's number opens the ledger's own
 * page. Zoho's Journal tab is the shape (the product owner's screenshot,
 * 2026-09-27).
 *
 * 🔨 The tile is withheld from a viewer the ledger's read refuses: the route is
 * the ledger's own gate, and a refusal draws nothing rather than a failure.
 */
export function AccountJournalTile({ sourceDocumentType, sourceDocumentId, apiBaseUrl = '' }: { sourceDocumentType: JournalSourceValue; sourceDocumentId: string; apiBaseUrl?: string }) {
  const fetched = useApi<DocumentJournalAnswer>(documentJournalPath(sourceDocumentType, sourceDocumentId), apiBaseUrl);
  if (fetched.denied) return null;
  const entries = fetched.state.kind === 'ready' ? fetched.state.data.entries : null;
  return (
    <section className={styles.tile} data-account-journal={sourceDocumentId}>
      <h2 className={styles.title}>{t('accountJournal.title')}</h2>
      <LoadFailure state={fetched.state} onRetry={fetched.reload} />
      {fetched.state.kind === 'empty' || (entries && entries.length === 0) ? <p className={styles.hint} data-account-journal-empty="true">{t('accountJournal.none')}</p> : null}
      {(entries ?? []).map((entry) => <JournalEntryBlock key={entry.id} entry={entry} />)}
    </section>
  );
}

function JournalEntryBlock({ entry }: { entry: DocumentJournalEntry }) {
  const lines: DocumentLinesDescriptor = {
    title: entry.entryNumber,
    rowAttribute: 'data-account-journal-line',
    columns: [t('accounting.journal.account'), t('accounting.journal.debit'), t('accounting.journal.credit')],
    money: false,
    rows: [
      ...entry.lines.map((row) => ({
        id: row.id,
        label: line(row.accountNumber, '·', row.accountName),
        cells: [
          { text: '', node: <Money cents={row.debitCents} />, attributes: { 'data-account-journal-debit': String(row.debitCents) } },
          { text: '', node: <Money cents={row.creditCents} />, attributes: { 'data-account-journal-credit': String(row.creditCents) } },
        ],
      })),
      {
        id: `${entry.id}-total`,
        label: t('accountJournal.total'),
        cells: [
          { text: '', node: <strong><Money cents={entry.debitTotalCents} /></strong>, attributes: { 'data-account-journal-debit-total': String(entry.debitTotalCents) } },
          { text: '', node: <strong><Money cents={entry.creditTotalCents} /></strong>, attributes: { 'data-account-journal-credit-total': String(entry.creditTotalCents) } },
        ],
      },
    ],
  };
  return (
    <div className={entry.isReversal ? `${styles.entry} ${styles.reversal}` : styles.entry} data-account-journal-entry={entry.id} data-account-journal-reversal={entry.isReversal ? 'true' : 'false'}>
      <p className={styles.heading}>
        {entry.isReversal ? line(t('accountJournal.reversal'), '· ') : null}
        <Link to={`${MANUAL_JOURNAL_ROUTE}/${entry.id}`} data-account-journal-open={entry.id}>{entry.entryNumber}</Link>
        {' '}{line('·', entry.accrualDate)}
      </p>
      {entry.memo ? <p className={styles.memo}>{entry.memo}</p> : null}
      <DocumentLines lines={lines} />
      {entry.reversals.map((reversal) => <JournalEntryBlock key={reversal.id} entry={reversal} />)}
    </div>
  );
}
